import _ from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";


export enum ScheduledReportsNotificationTabEnum {
    None = -1,
    Recipients = 0,
    NotificationLists = 1
}


export type ScheduledReportsNotificationStateType = {
    activeTab: ScheduledReportsNotificationTabEnum;
    recipientFilterText: string;
    notificationListFilterText: string;
    viewSelected: boolean;

    viewOptionState: ViewOptionStateEnum;

    
    organizationID: number | undefined;
    defaultOrganizationID: number | undefined;
};

enum ScheduledReportsNotificationActionType {
    ActiveTabChanged = "ACTIVE_TAB_CHANGED",
    RecipientFilterChanged = "RECIPIENT_FILTER_CHANGED",
    NotificationListFilterChanged = "NOTIFICATIONLIST_FILTER_CHANGED",
    ViewSelectedChanged = "VIEW_SELECTED_CHANGED",
    OrganizationChanged = 'ORGANIZATION_CHANGED',
    ResetChanged = "RESET_CHANGED",
}

type ScheduledReportsNotificationAction = {
    type: ScheduledReportsNotificationActionType.ActiveTabChanged;
    activeTab: ScheduledReportsNotificationTabEnum;
} | {
     type: ScheduledReportsNotificationActionType.RecipientFilterChanged;
     payload: string;
} | {
    type: ScheduledReportsNotificationActionType.NotificationListFilterChanged;
    payload: string;
} | {
    type: ScheduledReportsNotificationActionType.ViewSelectedChanged;
    payload: boolean;
} | {
    type: ScheduledReportsNotificationActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: ScheduledReportsNotificationActionType.ResetChanged;
    payload: void;
};

const initialState: ScheduledReportsNotificationStateType = {
    activeTab: ScheduledReportsNotificationTabEnum.Recipients,
    recipientFilterText: '',
    notificationListFilterText: '',
    viewSelected: false,
    viewOptionState: ViewOptionStateEnum.Default,
    organizationID: undefined,
    defaultOrganizationID: undefined
};

const optionsChanged = (newState: ScheduledReportsNotificationStateType, 
    defaultState: ScheduledReportsNotificationStateType) => 
    newState.recipientFilterText !== defaultState.recipientFilterText ||
    newState.notificationListFilterText !== defaultState.notificationListFilterText ||
    newState.viewSelected !== defaultState.viewSelected ||
    newState.organizationID !== newState.defaultOrganizationID;

function scheduledReportsNotificationsReducer(state: ScheduledReportsNotificationStateType, action: ScheduledReportsNotificationAction): ScheduledReportsNotificationStateType {
    let copy = _.cloneDeep(state);    

    switch (action.type) {

        case ScheduledReportsNotificationActionType.ActiveTabChanged:
            copy.activeTab = action.activeTab;
            break;

        case ScheduledReportsNotificationActionType.RecipientFilterChanged:
            copy.recipientFilterText = action.payload;
            break;

        case ScheduledReportsNotificationActionType.NotificationListFilterChanged:
            copy.notificationListFilterText = action.payload;
            break;
    
   
        case ScheduledReportsNotificationActionType.ViewSelectedChanged:
            copy.viewSelected = action.payload;
            break;

        case ScheduledReportsNotificationActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            if (copy.defaultOrganizationID === undefined) {
                copy.defaultOrganizationID = action.payload;
            }
            break;
                
        case ScheduledReportsNotificationActionType.ResetChanged:
            copy = _.cloneDeep(initialState);
            break;

        default:
            return state;
    }

    copy.viewOptionState = optionsChanged(copy, initialState) ? 
                            ViewOptionStateEnum.Changed : 
                            ViewOptionStateEnum.Default;

    return copy;
}

export type UseScheduledReportsNotificationStateType = {
    state: ScheduledReportsNotificationStateType;
    setActiveTab: (activeTab: ScheduledReportsNotificationTabEnum) => void;
    setRecipientFilterText: (value: string) => void;
    setNotificationListFilterText: (value: string) => void;
    setViewSelected: (value: boolean) => void;
    setOrganization: (value: number | undefined) => void;
    resetState: () => void;
};

export function useScheduledReportsNotificationState(): UseScheduledReportsNotificationStateType {

    const [state, dispatch] = useReducer(scheduledReportsNotificationsReducer, initialState);

    const setActiveTab = useCallback((activeTab: ScheduledReportsNotificationTabEnum) => dispatch({
        type: ScheduledReportsNotificationActionType.ActiveTabChanged,
        activeTab: activeTab
    }), []);

    const setRecipientFilterText = useCallback((value: string) => dispatch({
        type: ScheduledReportsNotificationActionType.RecipientFilterChanged,
        payload: value
    }), []);

    const setNotificationListFilterText = useCallback((value: string) => dispatch({
        type: ScheduledReportsNotificationActionType.NotificationListFilterChanged,
        payload: value
    }), []);

    const setViewSelected = useCallback((value: boolean) => dispatch({
        type: ScheduledReportsNotificationActionType.ViewSelectedChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: ScheduledReportsNotificationActionType.OrganizationChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: ScheduledReportsNotificationActionType.ResetChanged,
        payload: undefined
    }), [])

    return {
        state,
        setActiveTab,
        setRecipientFilterText,
        setNotificationListFilterText,
        setViewSelected,
        setOrganization,
        resetState
    };
}
